import { useEffect, useRef } from 'react';
import {
  CHIP_LABELS,
  type ChipCalendarEntry,
  type ChipExclusion,
  type ChipPlan,
  type ChipSuggestion,
  type GameweekPhase,
} from '@fantasy/types';
import { isChipInPlay } from '../lib/chipInPlay.ts';
import { Term } from './Term.tsx';
import { useGameweek } from '../lib/useGameweek.ts';

interface ChipCalendarProps {
  plan: ChipPlan;
}

/**
 * `TransferPlanView`'s sibling: where the squad's own blanks and doubles fall
 * over the horizon, and which gameweek each available chip best suits. Every
 * number here is deterministic output from `buildChipPlan` — this component
 * only lays it out, it doesn't judge it.
 */
export function ChipCalendar({ plan }: ChipCalendarProps) {
  const { horizon, entries, suggestions, excludedChips } = plan;
  const meta = useGameweek();

  // Same reasoning as TransferPlanView: this renders below a squad that can
  // run a full screen tall, so a result that just silently appears is easy to
  // miss without knowing to scroll. Re-fires on every fresh calendar, not
  // just the first, since a re-run is exactly as easy to lose track of.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [plan]);

  return (
    <section className="panel panel--accent-2 rise-in" aria-labelledby="chip-calendar-heading">
      <h3 id="chip-calendar-heading" ref={headingRef} tabIndex={-1}>
        The chip calendar
      </h3>
      <p className="hint">
        Over the next {horizon} gameweek{horizon === 1 ? '' : 's'}
      </p>

      <ol className="plan-timeline list-reset" aria-label="Blanks and doubles by gameweek">
        {entries.map((entry) => (
          <ChipCalendarEntryItem key={entry.gameweek} entry={entry} />
        ))}
      </ol>

      <h4>Chip suggestions</h4>
      {suggestions.length === 0 ? (
        <p className="callout-detail">You have no chips left to play — there's nothing left to plan for.</p>
      ) : (
        <ul className="list-reset chip-suggestions">
          {suggestions.map((suggestion) => (
            <ChipSuggestionItem key={suggestion.chip} suggestion={suggestion} />
          ))}
        </ul>
      )}
      <ChipExclusions
        excludedChips={excludedChips}
        currentGameweek={meta?.currentGameweek}
        phase={meta?.phase}
        // `entries[0]` is always `startGameweek` itself (`buildChipPlan`
        // builds the horizon starting there) — a fallback for exactly which
        // gameweek is "current" while `/meta/gameweek` hasn't resolved, same
        // reasoning as `ChipStatus`'s `squad.gameweek` fallback.
        knownCurrentGameweek={entries[0]?.gameweek}
      />
    </section>
  );
}

/**
 * Explains why the suggestion list above is shorter than four chips. A
 * suggestion never appears for a chip already spent — `buildChipPlan` has
 * nothing to plan there — so this is the only place that says so, right next
 * to the list a manager is reading when they notice the gap. Not
 * `ChipStatus`: that answers "where do I stand" for every chip on every
 * screen; this answers "why is this list short", scoped to the calendar.
 */
interface ChipExclusionsProps {
  excludedChips: ChipExclusion[] | null;
  currentGameweek: number | undefined;
  phase: GameweekPhase | null | undefined;
  knownCurrentGameweek: number | undefined;
}

/** Whether each excluded chip is still in play goes through `isChipInPlay`
 * (T-119), not `exclusion.active` read directly — the API resolves that
 * field from its own notion of "now", and this component must agree with
 * `ChipStatus` rather than each trusting its own source. */
function ChipExclusions({ excludedChips, currentGameweek, phase, knownCurrentGameweek }: ChipExclusionsProps) {
  if (excludedChips === null) {
    return <p className="hint">Chip history isn't known — link your FPL team to see what's already been played.</p>;
  }
  if (excludedChips.length === 0) return null;

  return (
    <ul className="list-reset chip-exclusions">
      {excludedChips.map((exclusion) => {
        const inPlay = isChipInPlay({ playedGameweek: exclusion.gameweek, currentGameweek, phase, knownCurrentGameweek });
        return (
          <li key={exclusion.chip} className="text-dim">
            {CHIP_LABELS[exclusion.chip]} — {inPlay ? 'in play this gameweek' : `played in gameweek ${exclusion.gameweek}`}
          </li>
        );
      })}
    </ul>
  );
}

function ChipCalendarEntryItem({ entry }: { entry: ChipCalendarEntry }) {
  return (
    <li className="plan-step chip-entry">
      <div className="plan-step-head">
        <span className="plan-step-gw">Gameweek {entry.gameweek}</span>
      </div>
      <p className="text-dim">
        <Counts blanking={entry.blanking} doubling={entry.doubling} /> ·{' '}
        {formatDifficulty(entry.meanDifficulty)}
      </p>
    </li>
  );
}

/** `blanking`/`doubling` are `null`, not 0, when no fixtures for this
 * gameweek have been ingested at all (T-118) — the same distinction
 * `meanDifficulty` already draws. Rendering the raw value put an empty string
 * where the number goes ("` blanking · ` doubling"), which reads as zero
 * rather than as unknown. */
function Counts({ blanking, doubling }: { blanking: number | null; doubling: number | null }) {
  if (blanking == null || doubling == null) return <>no fixtures known yet</>;
  return (
    <>
      {blanking} <Term term="blank">blanking</Term> · {doubling} <Term term="double">doubling</Term>
    </>
  );
}

/** `meanDifficulty` is `null`, not 0, when none of the squad's fixtures for
 * this gameweek have been ingested yet — rendered as "difficulty unknown"
 * rather than a number that would read as an easy week. */
function formatDifficulty(meanDifficulty: number | null | undefined): string {
  if (meanDifficulty == null) return 'difficulty unknown';
  return `difficulty ${meanDifficulty.toFixed(1)}`;
}

function ChipSuggestionItem({ suggestion }: { suggestion: ChipSuggestion }) {
  const label = CHIP_LABELS[suggestion.chip];

  return (
    <li className="plan-step chip-suggestion">
      <div className="plan-step-head">
        <span className="plan-step-gw">{label}</span>
        {suggestion.gameweek !== null && <span className="text-faint">Gameweek {suggestion.gameweek}</span>}
      </div>
      <p className="text-dim">{suggestion.reason}</p>
    </li>
  );
}
